import React, { useRef } from "react";
import QRCode from "react-qr-code";
import { useReactToPrint } from "react-to-print";
import { FaPrint, FaTimes, FaGift } from "react-icons/fa";
import { languageHelper } from "../../config/helperFunctions";

function GiftCardPrintModal({ card, onClose, currency = "SAR" }) {
  const printRef = useRef();

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `GiftCard-${card?.code || "Print"}`,
  });

  if (!card) return null;

  return (
    <div
      className="w-full h-screen grid place-items-center fixed top-0 left-0 bg-black bg-opacity-60 backdrop-blur-sm p-4 z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl max-w-xl w-full p-6 shadow-2xl space-y-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center border-b pb-3">
          <div className="flex items-center gap-2">
            <FaGift className="text-purple-600 text-xl" />
            <h2 className="text-xl font-bold text-gray-800">
              {languageHelper("Print Gift Card")}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100 transition"
          >
            <FaTimes size={18} />
          </button>
        </div>

        {/* Card Canvas for Print */}
        <div className="flex justify-center py-2">
          <div
            ref={printRef}
            className="w-[420px] h-[250px] rounded-2xl p-6 relative overflow-hidden shadow-xl text-white flex flex-col justify-between"
            style={{
              background:
                "linear-gradient(135deg, #0f172a 0%, #31104b 50%, #0f172a 100%)",
              border: "1px solid rgba(217, 119, 6, 0.4)",
              boxShadow: "0 10px 30px rgba(0,0,0,0.3)",
              fontFamily: "system-ui, -apple-system, sans-serif",
            }}
          >
            {/* Top decorative accent */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/10 rounded-full blur-2xl pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-purple-500/10 rounded-full blur-2xl pointer-events-none" />

            {/* Header */}
            <div className="flex justify-between items-start relative z-10">
              <div>
                <span className="text-[10px] tracking-widest text-amber-400 uppercase font-semibold">
                  {languageHelper("Exclusive Offer")}
                </span>
                <h3 className="text-lg font-extrabold tracking-wide text-white">
                  {card.title || languageHelper("GIFT CARD")}
                </h3>
              </div>
              <div className="text-right">
                <span className="text-xs text-gray-400 block font-medium">
                  {languageHelper("Card Value")}
                </span>
                <span className="text-xl font-black text-amber-400">
                  {currency} {Number(card.value || 0).toFixed(2)}
                </span>
              </div>
            </div>

            {/* Middle: Code & QR Code */}
            <div className="flex items-center justify-between gap-4 my-auto relative z-10">
              <div className="space-y-1">
                <span className="text-[11px] text-gray-400 uppercase tracking-wider block">
                  {languageHelper("Card Code")}
                </span>
                <div className="px-3 py-1.5 bg-black/40 border border-amber-400/30 rounded-lg text-amber-300 font-mono font-bold text-base tracking-widest select-all">
                  {card.code}
                </div>
                {card.remaining_balance !== undefined &&
                  card.remaining_balance !== card.value && (
                    <span className="text-[10px] text-emerald-400 block font-semibold mt-1">
                      {languageHelper("Remaining")}: {currency}{" "}
                      {Number(card.remaining_balance).toFixed(2)}
                    </span>
                  )}
              </div>

              <div className="bg-white p-2 rounded-xl shadow-md border border-gray-100 flex-shrink-0">
                <QRCode value={card.code || ""} size={72} />
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-between items-end border-t border-white/10 pt-2 text-[10px] text-gray-300 relative z-10">
              <div>
                <span className="text-gray-400 block">{languageHelper("Customer")}:</span>
                <span className="font-semibold text-white">
                  {card.customer?.name || languageHelper("All Customers / Bearer")}
                </span>
              </div>
              <div className="text-right">
                <span className="text-gray-400 block">{languageHelper("Expires On")}:</span>
                <span className="font-semibold text-amber-300">
                  {card.expiration_date ? card.expiration_date.split("T")[0] : "-"}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Modal Action Buttons */}
        <div className="flex justify-end gap-3 pt-2 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 font-medium transition"
          >
            {languageHelper("Close")}
          </button>
          <button
            onClick={handlePrint}
            className="px-5 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white font-medium flex items-center gap-2 shadow-md transition"
          >
            <FaPrint />
            {languageHelper("Print Card")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default GiftCardPrintModal;
